import * as React from "react";
import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils";

/**
 * Button — primary interactive element across the app.
 *
 * Variants match the visual patterns from the visitor page (legacy
 * `ActionBtn`, primary actions, danger actions, etc.).
 *
 * @example
 *   <Button variant="primary" size="md">Speichern</Button>
 *   <Button variant="secondary" size="sm">Abbrechen</Button>
 *   <Button variant="danger">Löschen</Button>
 *   <Button variant="ghost" size="icon"><PlusIcon /></Button>
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-[0.5rem] whitespace-nowrap font-medium transition-[background,border-color,filter,opacity] duration-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 font-[inherit]",
  {
    variants: {
      variant: {
        primary:
          "bg-primary text-foreground border border-primary hover:[filter:brightness(0.92)]",
        secondary:
          "bg-white text-foreground border border-gray hover:bg-[#f4f4f5]",
        danger:
          "bg-destructive text-white border border-destructive hover:[filter:brightness(0.92)]",
        dangerOutline:
          "bg-white text-destructive border border-[#fecaca] hover:bg-[#fef2f2]",
        ghost:
          "bg-transparent text-foreground border border-transparent hover:bg-[#f4f4f5]",
        link:
          "bg-transparent text-primary border border-transparent underline-offset-4 hover:underline px-0 h-auto",
      },
      size: {
        sm: "h-[3.2rem] px-[1rem] text-[1.15rem] rounded-btn",
        md: "h-[3.6rem] px-[1.4rem] text-[1.25rem] rounded-[0.8rem]",
        lg: "h-[4.4rem] px-[2rem] text-[1.4rem] rounded-[0.8rem] font-semibold",
        icon: "h-[3.6rem] w-[3.6rem] rounded-btn",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

const Button = React.forwardRef(
  ({ className, variant, size, type = "button", ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  ),
);
Button.displayName = "Button";

export { Button, buttonVariants };
